/**
 * judgeContent.js - this game's half of the judgement.
 *
 * PURE. Frames in, verdicts out.
 *
 * ---------------------------------------------------------------------------
 * THE DIVISION AT PHASE 1
 *
 *   A  Attends to Model       NOBODY. Observational, permanently. A microphone
 *                             cannot see whether a child is looking at the
 *                             screen, and every audio proxy for it (responded,
 *                             responded quickly) measures ability rather than
 *                             attention - so a capable inattentive child would
 *                             score well and a struggling attentive one badly,
 *                             inverting what the box is for. Never sent.
 *   B  Attempts Vocalization  the ENGINE, from the audio envelope. This game
 *                             declares `audio_energy` so that the envelope
 *                             exists; it does not judge the box. Never sent.
 *   C  Produces Target        THIS FILE.
 *
 * So `judgeAttempt()` returns exactly `{ C }`, and gameJudgedCodes(1) is
 * `['C']`. The set is asked for rather than assumed - see the note in
 * engine/contract.js - and a conformance check asserts that what this file
 * returns is what the engine expects at every level.
 *
 * Sending A or B would be refused with a 400 that the child never sees, and the
 * refusal is of the WHOLE attempt: a partial write leaves a permanent hole in
 * the chart that nothing later can fill.
 *
 * ---------------------------------------------------------------------------
 * IT IS TEMPTING TO PROXY THE OBSERVATIONAL BOX. DO NOT.
 *
 * This game is about attention more than any other on the platform - it is
 * literally about a child looking away from a screen - so "Attends to Model" is
 * the box its author most wants to fill in. There is a plausible-looking proxy
 * right there in the trial record: a child who answered before the first
 * remodel plainly heard the model. It is still wrong, for the reason above, and
 * the engine excludes the box from what this game owes. Inventing a proxy would
 * put a number in a therapist's chart that no sensor produced.
 *
 * ---------------------------------------------------------------------------
 * SUCCESS IS NOT THE SAME THING AS A BOX
 *
 * `success` is what the CHILD experiences - the person comes over. The box is
 * what the chart records. At Phase 1 they happen to be the same boolean, which
 * is exactly why it is worth saying: at Phase 3 they are not, and a game that
 * conflated them there would teach a child that half a request is a request.
 */

import { producedAny } from '../../shared/phrase.js';
import { acceptedPhrases, canonicalPhrase } from '../content/nearby.js';

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {{objectId:string}} input.target
 * @param {object|null} [input.trial]  NORMALISED trial record. Not read at
 *        Phase 1: every box that reads the record here is the engine's.
 * @param {string|null} [input.transcript]
 * @param {Array} [input.alternatives]
 * @returns {{components:Object<string,object>, success:boolean, produced:boolean,
 *            expected:string, heard:string|null, detail:object}}
 */
export function judgeAttempt({ phase, target, transcript = null, alternatives = [] }) {
  if (phase !== 1) {
    throw new Error(
      `Isaac No Youtube covers phase 1 only; asked to judge phase ${phase}. A game `
      + 'must never guess at a phase it does not implement - it would write a box '
      + 'that means something different from every other game.'
    );
  }

  const expected = canonicalPhrase(target);
  // The phase's strictness band is the ENGINE's (Phase 1 is `loose`:
  // approximations are accepted, "ap" for apple), and it is applied inside
  // producedAny() rather than here. A game that decided for itself how close
  // was close enough would make the same box mean two things.
  const hit = producedAny({
    phase: 1,
    targets: acceptedPhrases(target),
    transcript,
    alternatives,
  });

  return {
    components: {
      C: {
        success: hit.produced,
        expected,
        heard: hit.matched,
        reason: hit.reason,
        // Which accepted variant matched, so a run of "mom" for "mum" is
        // visible in the log rather than looking like a clean success.
        matchedVariant: hit.produced ? hit.target : null,
        matchedIn: hit.matchedIn,
      },
    },
    success: hit.produced,
    produced: hit.produced,
    expected,
    heard: hit.matched,
    detail: { rule: hit.rule, objectId: target.objectId },
  };
}

/** The component codes this game submits, per phase. */
export function gameJudgedCodes(phase) {
  return phase === 1 ? ['C'] : [];
}

export default { judgeAttempt, gameJudgedCodes };
